import { useEffect, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline'
import type { Attachment, MessageRead } from '../../client'
import {
  messageText,
  messageToolResults,
  type ToolResult,
} from '../../lib/messageContent'
import type { Segment } from './segments'
import Attachments from './Attachments'
import { useChat } from '../../providers/chat/context'
import FeedbackDialog from './FeedbackDialog'
import Markdown from './Markdown'
import ReasoningBlock from './ReasoningBlock'

const ACTION = 'rounded p-[3px] transition'

const TEST_BRANDS = (import.meta.env.VITE_TEST_ACCOUNTS ?? '')
  .split(',')
  .map((id: string) => id.trim())
  .filter(Boolean)

type Props = {
  message: MessageRead
  segs: Segment[]
  subMessages: MessageRead[]
  results: Map<string, ToolResult>
  showActions: boolean
  onPreview: (attachment: Attachment) => void
}

export default function ChatMessage({ message, segs, subMessages, results, showActions, onPreview }: Props) {
  const { brandId, setFeedback } = useChat()
  const [copied, setCopied] = useState(false)
  const [noting, setNoting] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => clearTimeout(copiedTimer.current ?? undefined), [])

  const text = messageText(message.content)
  const attachments = message.attachments ?? []

  if (message.role === 'user') {
    if (!text && attachments.length === 0) return null
    const isToolOutput = messageToolResults(message.content).length > 0
    if (isToolOutput) return <Attachments items={attachments} onPreview={onPreview} />
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col items-end gap-1.5">
          <Attachments items={attachments} align="end" onPreview={onPreview} />
          {text && (
            <div className="min-w-0 rounded-lg rounded-tr-sm bg-purple-100 px-2.5 py-1.5 text-xs whitespace-pre-wrap text-purple-900 [overflow-wrap:anywhere]">
              {text}
            </div>
          )}
        </div>
      </div>
    )
  }

  const subAttachments = subMessages.flatMap((sub) => sub.attachments ?? [])
  const allAttachments = [...attachments, ...subAttachments]

  if (segs.length === 0 && allAttachments.length === 0) return null

  function copy() {
    void navigator.clipboard.writeText(text)
    setCopied(true)
    clearTimeout(copiedTimer.current ?? undefined)
    copiedTimer.current = setTimeout(() => setCopied(false), 2000)
  }

  function download() {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'adlovin-response.md'
    document.body.append(link)
    link.click()
    link.remove()
    // Firefox needs the blob to outlive the click
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  function vote(value: 'up' | 'down') {
    void setFeedback(message.id, message.feedback === value ? null : value)
  }

  const canNote = TEST_BRANDS.includes(String(brandId ?? ''))
  const note = message.feedback && message.feedback !== 'up' && message.feedback !== 'down'

  return (
    <div className="flex w-full flex-col items-start gap-2">
      {segs.map((seg, index) =>
        seg.kind === 'reasoning' ? (
          <ReasoningBlock key={index} parts={seg.parts} results={results} />
        ) : (
          <Markdown key={index} text={seg.text} variant="bubble" />
        ),
      )}

      <Attachments items={allAttachments} onPreview={onPreview} />

      {noting && (
        <FeedbackDialog
          messageId={message.id}
          note={note ? String(message.feedback) : ''}
          onClose={() => setNoting(false)}
        />
      )}

      {text && showActions && (
        <div className="mt-0.5 flex items-center gap-px">
          <button
            type="button"
            title={copied ? 'Copied' : 'Copy'}
            onClick={copy}
            className={`${ACTION} text-stone-400 hover:bg-stone-200 hover:text-stone-600`}
          >
            {copied ? (
              <ClipboardDocumentCheckIcon className="size-3.5 text-green-500" />
            ) : (
              <ClipboardDocumentIcon className="size-3.5" />
            )}
          </button>
          <button
            type="button"
            title="Download"
            onClick={download}
            className={`${ACTION} text-stone-400 hover:bg-stone-200 hover:text-stone-600`}
          >
            <ArrowDownTrayIcon className="size-3.5" />
          </button>
          <span className="mx-0.5 h-3 w-px bg-stone-200" />
          {!note && (
            <>
          <button
            type="button"
            title="Helpful"
            onClick={() => vote('up')}
            className={`${ACTION} ${
              message.feedback === 'up'
                ? 'bg-green-100 text-green-500'
                : 'text-stone-400 hover:bg-stone-200 hover:text-stone-600'
            }`}
          >
            <HandThumbUpIcon className="size-3.5" />
          </button>
          <button
            type="button"
            title="Not helpful"
            onClick={() => vote('down')}
            className={`${ACTION} ${
              message.feedback === 'down'
                ? 'bg-red-100 text-red-500'
                : 'text-stone-400 hover:bg-stone-200 hover:text-stone-600'
            }`}
          >
            <HandThumbDownIcon className="size-3.5" />
          </button>
            </>
          )}
          {canNote && (
            <button
              type="button"
              title={note ? 'Edit feedback' : 'Leave feedback'}
              onClick={() => setNoting(true)}
              className={`${ACTION} ${
                note
                  ? 'bg-purple-100 text-purple-600'
                  : 'text-stone-400 hover:bg-stone-200 hover:text-stone-600'
              }`}
            >
              <ChatBubbleBottomCenterTextIcon className="size-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
